import { Link } from 'react-router-dom';
import { IconX } from '@tabler/icons-react';

import { Button } from '@components/common/Button';
import { Logo } from '@components/Logo';
import { PUBLIC_NAV_LINKS } from '@constants/publicSearch';
import { withPublicSiteView } from '@constants/publicSiteView';
import { useAuth } from '@contexts/AuthContext';
import { useEscapeKey } from '@hooks/useEscapeKey';
import { useRegistrationVisibility } from '@hooks/useRegistrationVisibility';

export interface MobileMenuProps {
  isOpen: boolean;
  isDarkMode: boolean;
  onClose: () => void;
  onToggleTheme: () => void;
  /** Signed-in member intentionally previewing the public site (`/?view=public`). */
  viewingPublicSite?: boolean;
}

export function MobileMenu({
  isOpen,
  isDarkMode,
  onClose,
  onToggleTheme,
  viewingPublicSite = false,
}: MobileMenuProps) {
  const { user, token, logout, isLoggingOut } = useAuth();
  const { showRegister } = useRegistrationVisibility();
  const isLoggedIn = Boolean(user && token);
  useEscapeKey(onClose, isOpen);

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  // Signed-in account buttons: Dashboard + Log out when previewing the public site, else Log out.
  const signedInAccountButtons = viewingPublicSite ? (
    <>
      <Link
        to="/dashboard"
        className="bhg-button bhg-button--primary bhg-button--md"
        onClick={onClose}
      >
        Dashboard
      </Link>
      <Button
        type="button"
        variant="ghost"
        size="md"
        loading={isLoggingOut}
        aria-busy={isLoggingOut}
        onClick={handleLogout}
      >
        {isLoggingOut ? 'Signing out…' : 'Log out'}
      </Button>
    </>
  ) : (
    <Button
      type="button"
      variant="ghost"
      size="md"
      loading={isLoggingOut}
      aria-busy={isLoggingOut}
      onClick={handleLogout}
    >
      {isLoggingOut ? 'Signing out…' : 'Log out'}
    </Button>
  );

  return (
    <>
      {/* Pointer-only backdrop; keyboard users close with Escape (useEscapeKey) or the close button. */}
      <div
        className={`bhg-mobile-menu-backdrop ${isOpen ? 'bhg-mobile-menu-backdrop--open' : ''}`}
        onClick={onClose}
        role="presentation"
        aria-hidden={!isOpen}
      />
      <aside
        id="bhg-mobile-menu"
        className={`bhg-mobile-menu ${isOpen ? 'bhg-mobile-menu--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        <div className="bhg-mobile-menu__header">
          <Logo
            variant={isDarkMode ? 'white' : 'primaryBlue'}
            size="small"
            showText={false}
          />
          <div className="bhg-mobile-menu__header-actions">
            <button
              type="button"
              className="bhg-mobile-menu__header-theme-btn"
              onClick={onToggleTheme}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="bhg-mobile-menu__close"
              onClick={onClose}
              aria-label="Close menu"
            >
              <IconX size={22} />
            </button>
          </div>
        </div>

        <div className="bhg-mobile-menu__body">
        <nav className="bhg-mobile-menu__nav" aria-label="Primary">
          {PUBLIC_NAV_LINKS.map((item) => {
            const { href, label, subLinks } = item;
            if (subLinks) {
              return (
                <div key={href} className="bhg-mobile-menu__group">
                  <Link
                    to={withPublicSiteView(href, viewingPublicSite)}
                    className="bhg-mobile-menu__link"
                    onClick={onClose}
                  >
                    {label}
                  </Link>
                  {subLinks.map((sub) => (
                    <Link
                      key={sub.href}
                      to={withPublicSiteView(sub.href, viewingPublicSite)}
                      className="bhg-mobile-menu__sublink"
                      onClick={onClose}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              );
            }
            return (
              <Link
                key={href}
                to={withPublicSiteView(href, viewingPublicSite)}
                className="bhg-mobile-menu__link"
                onClick={onClose}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="bhg-mobile-menu__divider" />

        <div className="bhg-mobile-menu__account">
          <div className="bhg-mobile-menu__section-title">Account</div>
          <div className="bhg-mobile-menu__account-buttons">
            {isLoggedIn ? signedInAccountButtons : (
              <>
                <Link
                  to="/login"
                  className="bhg-button bhg-button--ghost bhg-button--md"
                  onClick={onClose}
                >
                  Log in
                </Link>
                {showRegister ? (
                  <Link
                    to="/register"
                    className="bhg-button bhg-button--secondary bhg-button--md"
                    onClick={onClose}
                  >
                    Register
                  </Link>
                ) : null}
              </>
            )}
          </div>
        </div>
        </div>
      </aside>
    </>
  );
}
